/**
 * MessageBubble -- renders a single chat message (user or assistant).
 *
 * User messages are right-aligned with primary background.
 * Assistant messages are left-aligned with surface background and markdown-rendered.
 * Tool call results render as ActionCard components below the bubble.
 */

import Markdown from "react-markdown";
import type { ChatMessage } from "../../hooks/use-chat";
import { ActionCard } from "./action-card";

interface MessageBubbleProps {
  message: ChatMessage;
}

export function MessageBubble({ message }: MessageBubbleProps): React.ReactElement {
  const isUser = message.role === "user";

  return (
    <div className={`flex flex-col ${isUser ? "items-end" : "items-start"} mb-3`}>
      {/* Message bubble */}
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
          isUser
            ? "bg-primary text-white rounded-br-md"
            : "bg-surface text-text-primary rounded-bl-md"
        }`}
      >
        {message.content && (
          isUser ? (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="text-sm prose prose-sm prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-2 prose-code:bg-surface-active prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-pre:bg-surface-active prose-pre:p-3 prose-strong:text-text-primary">
              <Markdown>{message.content}</Markdown>
            </div>
          )
        )}
        <span
          className={`block text-xs font-mono mt-1 ${
            isUser ? "opacity-60" : "opacity-60"
          }`}
        >
          {new Date(message.createdAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>

      {/* Tool call indicators (running) */}
      {message.toolCalls
        ?.filter((tc) => tc.type === "start")
        .map((tc, i) => (
          <span
            key={`start-${tc.toolName}-${i}`}
            className="text-xs text-text-tertiary italic mt-1"
          >
            Running {tc.toolName}...
          </span>
        ))}

      {/* Action cards for completed tool calls */}
      {message.toolCalls
        ?.filter((tc) => tc.type === "end")
        .map((tc, i) => (
          <ActionCard
            key={`end-${tc.toolName}-${i}`}
            toolName={tc.toolName}
            result={tc.result ?? ""}
          />
        ))}
    </div>
  );
}
